import { useEffect, useState, useCallback, useRef } from 'react';

/**
 * Custom hook for throttled scroll events
 * Limits scroll handler execution to max 60fps (every ~16ms)
 */
export const useThrottledScroll = (callback: (scrollY: number) => void, delay: number = 16) => {
  const [scrollY, setScrollY] = useState(0);
  const lastExecutionRef = useRef(0);
  const tickingRef = useRef(false);

  const handleScroll = useCallback(() => {
    if (!tickingRef.current) {
      requestAnimationFrame(() => {
        const now = Date.now();
        
        if (now - lastExecutionRef.current >= delay) {
          const currentScrollY = window.scrollY;
          setScrollY(currentScrollY);
          callback(currentScrollY);
          lastExecutionRef.current = now;
        }
        
        tickingRef.current = false;
      });
      tickingRef.current = true;
    }
  }, [callback, delay]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial call
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return scrollY;
};
